<template>
  <Loader :show="pageLoading" v-if="pageLoading" />
  <MDBCard class="border-0" v-else>
    <CategoryTop small :title="$t('account.profile.title')" :breadcrumbs-parent-routes="['account']"
      :description="!tenantConfig.readOnlyProfile ? $t('account.profile.description') : $t('account.profile.block_description')"
      content-class="px-4 mt-4" left-col="6" right-col="6">
      <template #right>
        <MDBRow v-if="!tenantConfig.readOnlyProfile">
          <MDBCol col="12" class="mb-5">
            <div class="progress">
              <div class="progress-bar bg-danger progress-bar-striped progress-bar-animated" role="progressbar"
                :style="'width:' + fillPercent + '%'" :aria-valuenow="fillPercent" aria-valuemin="0"
                aria-valuemax="100">
                {{ fillPercent }}%
              </div>
            </div>
          </MDBCol>
        </MDBRow>
      </template>
    </CategoryTop>
    <template v-if="tenantConfig.readOnlyProfile">
      <ReadOnlyLiteProfile />
    </template>
    <MDBTabs v-model="activeTabId" v-else>
      <MDBRow>
        <MDBCol md="12" class="px-5 pb-4">
          <!-- Tabs navs -->
          <MDBTabNav tabsClasses="profile-tabs" v-if="isLite" pills>
            <MDBTabItem
              v-for="tab in profileTabs"
              :key="tab.id"
              :tabId="tab.id"
              :href="tab.id"
              class="text-center py-1"
              :class="`${activeTabId === tab.id
                ? 'bg-dark text-white'
                : 'bg-secondary-gray text-dark'
                }`"
            >
              <div
                v-if="tab.html"
                class="item--height d-flex justify-content-center align-items-center"
                v-html="$t(tab.labelKey)"
              ></div>
              <div v-else class="item--height d-flex justify-content-center align-items-center">
                {{ $t(tab.labelKey) }}
              </div>
            </MDBTabItem>
          </MDBTabNav>
        </MDBCol>
      </MDBRow>


      <MDBTabContent>
        <MDBTabPane tabId="general">
          <VeeForm class="form user-form" @submit="onSubmit">
            <MDBRow>
              <MDBCol xl="4" md="6" class="mb-4">
                <PersonalData ref="personalData" :showDots="true"></PersonalData>
              </MDBCol>

              <MDBCol xl="4" md="6" class="mb-4">
                <OperatingData ref="operatingData"></OperatingData>
              </MDBCol>

              <MDBCol xl="3" md="6" class="mb-4">
                <div>
                  <label for="legalForm" class="label px-3 text-uppercase pt-5">
                    {{ $t("account.profile.legalForm") }}
                  </label>
                  <input type="text" class="form-control" :placeholder="$t('account.profile.legalForm')"
                    :aria-label="$t('account.profile.legalForm')" aria-describedby="basic-addon1" v-model="legalForm"
                    id="legalForm" />
                </div>

              </MDBCol>

              <MDBCol xl="12" md="6" class="mb-4" v-if="showTenantEmails()">
                <TenantEmail ref="tenantEmails" />
              </MDBCol>
            </MDBRow>

            <MDBRow class="pb-3" :class="isLite ? 'd-none' : ''" v-if="!isLite">
              <MDBCol col="12" class="mb-4">
                <IndicatorsData ref="indicatorsData"></IndicatorsData>
              </MDBCol>
              <MDBCol col="12" class="mb-4">
                <Field type="checkbox" name="conditions" v-model="conditions" :value="true" :unchecked-value="false"
                  class="form-check-input" :label="$t('account.profile.acceptTerms')" :rules="{ required: true }">
                </Field>
                <span class="px-2 text-decoration-underline--hover cursor--pointer" @click="pushToRules()">{{
                  $t("account.profile.acceptTerms") }}</span>
                <div>
                  <ErrorMessage name="conditions" class="text-danger text-small text-end m-0 pt-1" />
                </div>
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol col="12" class="text-start">
                <div v-html="$t('account.profile.newCustomerNumbersInfo')" class="pb-3"
                  v-if="!isWholesaler && !isStaff && !isLite">
                </div>
                <div v-if="!isLite">
                  {{ $t('account.profile.contactInfo', { email: tenantConfig.contactEmail }) }}
                </div>

                <MDBBtn type="submit" color="danger" :disabled="loading"
                  class="d-flex justify-content-center align-items-center">
                  <span>{{ $t("global.buttons.send") }}</span>
                  <Loader :show="loading" :small="true" spinner="spinner.svg" :tiny="true" />
                </MDBBtn>
                <p v-if="apiError" class="text-danger text-small text-end m-0 pt-2 text-small">
                  {{ apiError }}
                </p>
              </MDBCol>
            </MDBRow>
          </VeeForm>
        </MDBTabPane>
        <MDBTabPane tabId="preference">
          <LiteVersionPreferences v-if="isLite" ref="preferencesRef" @requestSave="onSubmit"
            @customerNumberExists="activeTabId = 'preference'" />
        </MDBTabPane>
      </MDBTabContent>
    </MDBTabs>
  </MDBCard>
</template>

<script setup>
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import CategoryTop from "@/components/General/CategoryTop/index.vue";
import { MDBRow, MDBCol, MDBCard, MDBBtn, MDBTabs, MDBTabNav, MDBTabItem, MDBTabContent, MDBTabPane } from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { ref, onMounted, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import ReadOnlyLiteProfile from "@/components/User/Profile/ReadOnlyLiteProfile.vue";
import PersonalData from "@/components/User/ProfileForms/PersonalData.vue";
import OperatingData from "@/components/User/ProfileForms/OperatingData.vue";
import IndicatorsData from "@/components/User/ProfileForms/IndicatorsData.vue";
import TenantEmail from "@/components/User/ProfileForms/TenantEmail.vue";
import LiteVersionPreferences from "@/components/User/ProfileForms/LiteVersionPreferences.vue";
import Loader from "@/components/General/Loader/index.vue";

import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useTenantConfig } from "@/composables/useTenantConfig";
import { userCanAddTenantEmails } from "@/composables/useProfileFieldCheck";
import textTransform from "@/plugins/textTransform";

import { TextPageService } from "@/services/textPage.service";
import { UserService } from "@/services/user.service";

import { afterSuccess } from "@/composables/profile.js";
import { useProfileFormData } from "@/composables/useProfileFormData";
import { useProfileFillPercent } from "@/composables/useProfileFillPercent";

const store = useStore();
const { me, userHashId, profileHashId, userPlainId, loadProfileForm } =
  useProfileFormData(store);
const router = useRouter();
const { tenantConfig } = useTenantConfig();
const { showTenantEmails } = userCanAddTenantEmails(tenantConfig);
// Tenant Configuration
const { t } = useI18n(); // Import the translation function from i18n
const personalData = ref(null);
const operatingData = ref(null);
const indicatorsData = ref(null);
const apiError = ref(null);
const legalForm = ref(null);
const isWholesaler = ref(false);
const isWholesalerStaff = ref(false);
const isStaff = ref(false);
const conditions = ref(false);
const tenantEmails = ref([])
const loading = computed(() => store.getters.getPending);
const isLite = computed(() => store.getters.isLiteVersion);
const profileTabs = computed(() => {
  const tabs = [
    { id: "general", labelKey: "account.profile.general", html: true },
    { id: "preference", labelKey: "account.profile.activityAndProfile", html: false },
  ];

  return tenantConfig.value?.swapProfileTabs ? [...tabs].reverse() : tabs;
});
const activeTabId = ref(
  tenantConfig.value?.swapProfileTabs ? "preference" : "general",
);
const pageLoading = ref(true)
const preferencesRef = ref(null);

const onSubmit = async () => {
  if (!personalData.value?.models?.forename || !personalData.value?.models?.name) {
    apiError.value = t('onboarding.requiredFields');
    activeTabId.value = 'general';
    return
  }
  const { vat, email, id, ...personal } = personalData.value.models;
  const { ...operating } = operatingData.value.models;
  const { lang, ...mainAddress } = operating;

  let args = {
    id,
    user: {
      email,
      country: operating.country,
      lang,
      vat,
      is_active: true,
    },
    profile: {
      user_id: id,
      legalForm: legalForm.value,
      ...personal,
      ...mainAddress,
      ...indicatorsData.value?.models,
      conditions: conditions.value
    },
    addresses: [mainAddress],
  };

  if (args.profile.mobile_phone !== null && args.profile.mobile_phone !== undefined) {
    args.profile.mobile_phone = String(args.profile.mobile_phone);
  }


  if (args.user.country !== "ch") {
    args.user.lang = "de";
  }
  const response = await store.dispatch("updateUser", args);

  if (response.status === 200) {
    if (showTenantEmails()) {
      const data = {
        user_id: me.value.plainId,
        tenant_emails: tenantEmails.value.emails
      }
      await UserService.tenantEmail(data)
    }

    let preferencesOk = true;
    if (preferencesRef.value?.persistPreferences) {
      preferencesOk = await preferencesRef.value.persistPreferences({
        refresh: false,
        showToast: false,
      }) !== false;
    }

    if (preferencesOk) {
      await loadProfileForm();
      apiError.value = null;
      toast(t("toast.profileSaved"), {
        type: "success",
        theme: "colored",
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }

    await afterSuccess(
      userHashId.value,
      profileHashId.value,
      fillPercent.value,
      userPlainId.value,
      store
    );
  } else {
    apiError.value = response.data.message;
  }
};

const { fillPercent } = useProfileFillPercent(() => {
  const legalFormPercentage = legalForm.value ? 100 : 0;

  if (!isLite.value) {
    return [
      personalData.value?.percentage,
      operatingData.value?.percentage,
      indicatorsData.value?.percentage,
      legalFormPercentage,
    ];
  }

  return [
    personalData.value?.percentage,
    operatingData.value?.percentage,
    legalFormPercentage,
  ];
});

watch(me, async (newVal) => {
  if (newVal && newVal.roles.find((item) => item.name == "wholesaler")) {
    isWholesaler.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "wholesalerStaff")) {
    isWholesalerStaff.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "wholesaler")) {
    isStaff.value = true;
  }
});


watch(
  () => store.getters.getProfile,
  (newVal) => {
    if (!newVal) return;
    legalForm.value = newVal.legalForm;
  }
);


onMounted(async () => {
  try {
    await loadProfileForm();
  } catch (error) {
    console.warn(error);
  } finally {
    pageLoading.value = false;
  }
});

const pushToRules = async () => {
  const response = await TextPageService.textPageBySlug("terms-and-conditions");

  if (response.status == 200) {
    router.push({
      name: "text_page",
      params: {
        id: response.data.data.id,
        slug: textTransform.convertToSlug(response.data.data.title),
      },
    });
  }
};

</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/profile.scss";

.item--height {
  min-height: 40px !important;
}
</style>
